import React, { useState } from "react";
import { Phone, MessageCircle, Mail, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", service: "Sublimation T-Shirts", qty: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone so we can reach you.");
      return;
    }
    // Compose WhatsApp message and open it
    const text = `Hi Customade! I'd like a quote.\n\nName: ${form.name}\nPhone: ${form.phone}\nService: ${form.service}\nQty: ${form.qty || "-"}\n\n${form.message || ""}`.trim();
    const waUrl = `https://wa.me/918169272803?text=${encodeURIComponent(text)}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
    setSubmitted(true);
    toast.success("Opening WhatsApp — we'll reply in under an hour!");
  };

  return (
    <section
      id="contact"
      className="relative border-b-4 border-black bg-[#FFE600]"
      data-testid="contact-section"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 py-20 lg:py-28 grid grid-cols-12 gap-8 lg:gap-12">
        {/* Left: massive call-out */}
        <div className="col-span-12 lg:col-span-6">
          <div className="font-heading uppercase text-xs tracking-[0.3em] mb-4">/// Let's make something</div>
          <h2 className="font-heading uppercase text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.9] tracking-tighter">
            Ready to
            <br />
            <span className="bg-black text-[#FFE600] px-2 inline-block">print?</span>
          </h2>
          <p className="mt-6 max-w-md text-lg leading-relaxed">
            Tap to call, drop a WhatsApp, or fill the form. You'll hear back within the hour, promise.
          </p>

          <div className="mt-10 space-y-4 max-w-md">
            <a
              href="tel:8169272803"
              className="flex items-center justify-between nb-border-4 nb-shadow bg-black text-[#FFE600] px-5 py-4 hover:-translate-x-1 hover:-translate-y-1 transition-transform"
              data-testid="contact-phone-cta"
            >
              <span className="flex items-center gap-3">
                <Phone size={22} strokeWidth={3} />
                <span className="font-heading uppercase text-xl">Call 8169272803</span>
              </span>
              <ArrowRight size={20} strokeWidth={3} />
            </a>
            <a
              href="https://wa.me/918169272803"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between nb-border-4 nb-shadow bg-white px-5 py-4 hover:-translate-x-1 hover:-translate-y-1 transition-transform"
              data-testid="contact-whatsapp-cta"
            >
              <span className="flex items-center gap-3">
                <MessageCircle size={22} strokeWidth={3} className="text-[#FF0055]" />
                <span className="font-heading uppercase text-xl">WhatsApp Us</span>
              </span>
              <ArrowRight size={20} strokeWidth={3} />
            </a>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md text-sm">
            <div className="flex items-start gap-3">
              <Mail size={18} strokeWidth={2.5} className="mt-0.5" />
              <div>
                <div className="font-heading uppercase text-xs tracking-wider text-[#555]">Email</div>
                <div className="font-medium">hello@customade.in</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin size={18} strokeWidth={2.5} className="mt-0.5" />
              <div>
                <div className="font-heading uppercase text-xs tracking-wider text-[#555]">Location</div>
                <div className="font-medium">Mumbai, India</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: form */}
        <div className="col-span-12 lg:col-span-6">
          <form
            onSubmit={onSubmit}
            className="bg-white nb-border-4 nb-shadow-lg p-6 md:p-8"
            data-testid="quote-form"
          >
            <div className="font-heading uppercase text-2xl md:text-3xl mb-6">Get a quick quote</div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="font-heading uppercase text-xs tracking-wider">Your Name *</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  placeholder="Neha Sharma"
                  className="mt-1 w-full nb-border-2 bg-white px-3 py-3 font-body focus:outline-none focus:bg-[#FFE600]/30"
                  data-testid="form-name"
                />
              </div>
              <div>
                <label className="font-heading uppercase text-xs tracking-wider">Phone / WhatsApp *</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  required
                  inputMode="tel"
                  placeholder="98XXXXXXXX"
                  className="mt-1 w-full nb-border-2 bg-white px-3 py-3 font-body focus:outline-none focus:bg-[#FFE600]/30"
                  data-testid="form-phone"
                />
              </div>
              <div>
                <label className="font-heading uppercase text-xs tracking-wider">Service</label>
                <select
                  name="service"
                  value={form.service}
                  onChange={onChange}
                  className="mt-1 w-full nb-border-2 bg-white px-3 py-3 font-body focus:outline-none focus:bg-[#FFE600]/30"
                  data-testid="form-service"
                >
                  <option>Sublimation T-Shirts</option>
                  <option>Cotton Round-Neck Tees</option>
                  <option>Cotton Polos (Embroidery)</option>
                  <option>Cotton Fleece Hoodies</option>
                  <option>Oversized T-Shirts</option>
                  <option>Industrial Boiler Suits</option>
                  <option>Windcheaters / Jackets</option>
                  <option>Uniforms</option>
                  <option>Promotional T-Shirts</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="font-heading uppercase text-xs tracking-wider">Quantity</label>
                <input
                  name="qty"
                  value={form.qty}
                  onChange={onChange}
                  placeholder="e.g. 50"
                  className="mt-1 w-full nb-border-2 bg-white px-3 py-3 font-body focus:outline-none focus:bg-[#FFE600]/30"
                  data-testid="form-qty"
                />
              </div>
              <div className="md:col-span-2">
                <label className="font-heading uppercase text-xs tracking-wider">Tell us more (optional)</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={3}
                  placeholder="Event date, size breakup, design ideas…"
                  className="mt-1 w-full nb-border-2 bg-white px-3 py-3 font-body focus:outline-none focus:bg-[#FFE600]/30 resize-none"
                  data-testid="form-message"
                />
              </div>
            </div>

            <button
              type="submit"
              className="mt-6 nb-btn nb-btn-magenta w-full justify-center text-base"
              data-testid="form-submit"
            >
              Send via WhatsApp <ArrowRight size={18} strokeWidth={3} />
            </button>

            {submitted && (
              <div
                className="mt-4 nb-border-2 bg-[#00F0FF]/40 px-3 py-2 text-sm"
                data-testid="form-success"
              >
                We opened WhatsApp in a new tab. If it didn't, call us at 8169272803.
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
